/* Customer portal — Invoices list + PDF download. */
import { useEffect, useState } from "react";
import { api, API_BASE, apiHref } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Receipt } from "lucide-react";

const statusStyle = {
  paid:    "bg-emerald-100 text-emerald-700 border-emerald-300",
  unpaid:  "bg-amber-100 text-amber-700 border-amber-300",
  overdue: "bg-red-100 text-red-700 border-red-300",
};

export default function PortalInvoices() {
  const [list, setList] = useState([]);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/portal/invoices"); setList(r.data); } catch (e) { console.warn("portal invoices load failed", e); }
    })();
  }, []);

  const openPdf = (id) => window.open(apiHref(`/portal/invoices/${id}/pdf`), "_blank");
  const total = list.filter((i) => i.status !== "paid").reduce((s, i) => s + Number(i.total || 0), 0);

  return (
    <div className="space-y-8" data-testid="portal-invoices">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-neutral-500">Billing</div>
          <h1 className="text-4xl font-black">Invoices</h1>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-widest uppercase text-neutral-500">Outstanding</div>
          <div className="mono text-3xl font-black text-primary-orange" data-testid="portal-invoices-total-outstanding">${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
        </div>
      </div>

      {list.length === 0 && (
        <div className="bg-white border border-neutral-200 p-10 text-center text-neutral-500">No invoices yet.</div>
      )}
      {list.length > 0 && (
        <div className="bg-white border border-neutral-200 divide-y divide-neutral-100">
          {list.map((inv) => (
            <div key={inv.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`portal-invoice-${inv.id}`}>
              <div className="w-10 h-10 bg-primary-orange/10 text-primary-orange flex items-center justify-center rounded shrink-0">
                <Receipt className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold">{inv.invoice_no}</div>
                <div className="text-xs text-neutral-500">Issued {(inv.issued_at || "").slice(0, 10)} · Due {inv.due_at || "—"}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${statusStyle[inv.status] || statusStyle.unpaid}`}>{inv.status}</span>
              <div className="text-right w-28">
                <div className="mono font-black">${Number(inv.total).toFixed(2)}</div>
                <div className="text-[10px] text-neutral-500 mono">{inv.lines?.length || 0} lines</div>
              </div>
              <Button data-testid={`invoice-pdf-${inv.id}`} onClick={() => openPdf(inv.id)} variant="outline" className="rounded-none border-neutral-300 uppercase tracking-widest text-xs">
                <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
